import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import ServiceCard from './components/ServiceCard';
import Header from './components/Header';
import IRCBotCard from './components/IRCBotCard';
import TrackerStats from './components/TrackerStats';
import History from './components/History';
import { Service, ServiceStatus, IRCStatus } from './types/Service';

interface TrackerStatsData {
  // Torrents
  totalTorrents: number;
  totalTorrentsSize: string;
  
  // Category breakdown
  movies: number;
  tv: number;
  music: number;
  anime: number;
  games: number;
  apps: number;
  sport: number;
  audiobook: number;
  ebook: number;
  hobby: number;
  musicProduction: number;
  other: number;
  fanres?: number;
  trailer?: number;
  liveConcert?: number;
  
  // Resolution breakdown
  res4320p: number;
  res2160p: number;
  res1080p: number;
  res1080i: number;
  res720p: number;
  res576p: number;
  res576i: number;
  res480p: number;
  res480i: number;
  resUpscale: number;
  resOther: number;
  
  // Users
  totalUsers: number;
  disabledUsers: number;
  prunedUsers: number;
  bannedUsers: number;
  activeToday: number;
  activeThisWeek: number;
  activeThisMonth: number;
  activeAll: number;
  
  // Peers
  totalSeeders: number;
  totalLeechers: number;
  totalPeers: number;
  seedSize: string;
  
  // Traffic - Real
  realUpload: string;
  realDownload: string;
  realTotal: string;
  
  // Traffic - Credited
  creditedUpload: string;
  creditedDownload: string;
  creditedTotal: string;
  
  lastUpdated: Date;
}

const MainContent: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [ircStatus, setIrcStatus] = useState<IRCStatus | null>(null);
  const [seedpoolStats, setSeedpoolStats] = useState<TrackerStatsData | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [maintenanceMode] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const currentPage = location.pathname === '/stats'
    ? 'stats'
    : location.pathname === '/history'
      ? 'history'
      : 'seedpool';
  const [seedpoolLoading, setSeedpoolLoading] = useState(false);
  const [seedpoolError, setSeedpoolError] = useState<string | null>(null);

  // Define the services to monitor
  const serviceConfigs: Omit<Service, 'status' | 'lastChecked' | 'responseTime'>[] = [
    {
      id: 'seedpool-main',
      name: 'Seedpool.org',
      url: 'https://seedpool.org',
      description: 'Main Seedpool website',
      category: 'Web Services'
    },
    {
      id: 'seedpool-tracker',
      name: 'Tracker',
      url: 'https://seedpool.org/announce',
      description: 'Tracker Endpoint',
      category: 'API Services'
    },
    {
      id: 'seedpool-forums',
      name: 'Forums',
      url: 'https://forums.seedpool.org',
      description: 'Seedpool community forums',
      category: 'Web Services'
    },
    {
      id: 'irc-service',
      name: 'IRC Service',
      url: 'irc.seedpool.org:6697',
      description: 'IRC monitoring backend',
      category: 'Backend Services'
    },
    {
      id: 'tracker-service',
      name: 'Tracker Service',
      url: `/api/seedpool/stats`,
      description: 'Seedpool statistics service',
      category: 'Backend Services'
    }
  ];

  const checkServiceStatus = async (service: Omit<Service, 'status' | 'lastChecked' | 'responseTime'>): Promise<Service> => {
    const startTime = Date.now();
    
    // In maintenance mode, only show Seedpool.org as offline
    if (maintenanceMode && service.id === 'seedpool-main') {
      return {
        ...service,
        status: 'offline' as ServiceStatus,
        lastChecked: new Date(),
        responseTime: 0
      };
    }
    
    // For external services, use our backend to check them server-side
    if (service.id !== 'irc-service' && service.id !== 'tracker-service') {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        const response = await fetch(`/api/service-check/${service.id}`, {
          method: 'GET',
          signal: controller.signal,
          mode: 'cors'
        });

        clearTimeout(timeoutId);
        const responseTime = Date.now() - startTime;

        if (response.ok) {
          const data = await response.json();
          return {
            ...service,
            status: data.status as ServiceStatus,
            lastChecked: new Date(data.lastChecked),
            responseTime: data.responseTime
          };
        } else {
          return {
            ...service,
            status: 'down' as ServiceStatus,
            lastChecked: new Date(),
            responseTime
          };
        }
      } catch (error) {
        const responseTime = Date.now() - startTime;
        return {
          ...service,
          status: 'down' as ServiceStatus,
          lastChecked: new Date(),
          responseTime
        };
      }
    }
    
    // For the IRC service, make the actual request to our backend
    if (service.id === 'irc-service') {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(`/api/irc/status`, {
          method: 'GET',
          signal: controller.signal,
          mode: 'cors'
        });

        clearTimeout(timeoutId);
        const responseTime = Date.now() - startTime;

        if (response.ok) {
          const ircData = await response.json();
          return {
            ...service,
            status: ircData.connected ? 'operational' as ServiceStatus : 'down' as ServiceStatus,
            lastChecked: new Date(),
            responseTime
          };
        } else {
          return {
            ...service,
            status: 'down' as ServiceStatus,
            lastChecked: new Date(),
            responseTime
          };
        }
      } catch (error) {
        const responseTime = Date.now() - startTime;
        return {
          ...service,
          status: 'down' as ServiceStatus,
          lastChecked: new Date(),
          responseTime
        };
      }
    }
    
    // For the Tracker service, check one of the tracker API endpoints
    if (service.id === 'tracker-service') {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(`/api/seedpool/stats?t=${Date.now()}`, {
          method: 'GET',
          signal: controller.signal,
          mode: 'cors'
        });

        clearTimeout(timeoutId);
        const responseTime = Date.now() - startTime;

        if (response.ok) {
          return {
            ...service,
            status: 'operational' as ServiceStatus,
            lastChecked: new Date(),
            responseTime
          };
        } else {
          return {
            ...service,
            status: 'down' as ServiceStatus,
            lastChecked: new Date(),
            responseTime
          };
        }
      } catch (error) {
        const responseTime = Date.now() - startTime;
        return {
          ...service,
          status: 'down' as ServiceStatus,
          lastChecked: new Date(),
          responseTime
        };
      }
    }
    
    // Default fallback (should never reach here with current service configs)
    const responseTime = Date.now() - startTime;
    return {
      ...service,
      status: 'unknown' as ServiceStatus,
      lastChecked: new Date(),
      responseTime
    };
  };

  const checkIRCStatus = async (): Promise<IRCStatus | null> => {
    try {
      const response = await fetch(`/api/irc/status?t=${Date.now()}`);
      if (response.ok) {
        const data = await response.json();
        
        // Parse bot lastSeen dates
        const parsedBots: { [key: string]: any } = {};
        Object.entries(data.bots).forEach(([botName, bot]: [string, any]) => {
          parsedBots[botName] = {
            ...bot,
            lastSeen: bot.lastSeen ? new Date(bot.lastSeen) : null
          };
        });
        
        // In maintenance mode, set specific bots to offline
        if (maintenanceMode) {
          const maintenanceBots = ['Banker', 'Dealer', 'Bob', 'DM'];
          maintenanceBots.forEach(botName => {
            if (parsedBots[botName]) {
              parsedBots[botName] = {
                ...parsedBots[botName],
                online: false,
                lastSeen: null
              };
            }
          });
          
          // Update online count
          const onlineCount = Object.values(parsedBots).filter((bot: any) => bot.online).length;
          data.onlineBots = onlineCount;
        }
        
        // Calculate onlineBots and totalBots if not provided
        const onlineBots = data.onlineBots !== undefined ? data.onlineBots : Object.values(parsedBots).filter((bot: any) => bot.online).length;
        const totalBots = data.totalBots !== undefined ? data.totalBots : Object.keys(parsedBots).length;
        
        return {
          ...data,
          bots: parsedBots,
          onlineBots: onlineBots,
          totalBots: totalBots,
          lastUpdate: new Date(data.lastUpdate)
        };
      }
    } catch (error) {
      console.error('Error checking IRC status:', error);
    }
    return null;
  };

  const checkSeedpoolStats = async (): Promise<TrackerStatsData | null> => {
    try {
      setSeedpoolLoading(true);
      setSeedpoolError(null);
      
      const response = await fetch(`/api/seedpool/stats?t=${Date.now()}`);
      if (response.ok) {
        const data = await response.json();
        return {
          ...data,
          lastUpdated: new Date(data.lastUpdated)
        };
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error checking Seedpool stats:', error);
      setSeedpoolError(error instanceof Error ? error.message : 'Unknown error');
      return null;
    } finally {
      setSeedpoolLoading(false);
    }
  };

  const checkAllServices = useCallback(async () => {
    setIsLoading(true);
    try {
      const servicePromises = serviceConfigs.map(checkServiceStatus);
      const ircStatusPromise = checkIRCStatus();
      const seedpoolStatsPromise = currentPage === 'stats' ? checkSeedpoolStats() : Promise.resolve(null);
      
      const [updatedServices, ircData, seedpoolData] = await Promise.all([
        Promise.all(servicePromises),
        ircStatusPromise,
        seedpoolStatsPromise
      ]);
      
      setServices(updatedServices);
      setIrcStatus(ircData);
      if (seedpoolData) {
        setSeedpoolStats(seedpoolData);
      }
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error checking services:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage]);

  const handlePageChange = (page: 'seedpool' | 'stats' | 'history') => {
    if (page === 'stats') {
      navigate('/stats');
      if (!seedpoolStats) {
        checkSeedpoolStats();
      }
    } else if (page === 'history') {
      navigate('/history');
    } else {
      navigate('/');
    }
  };

  useEffect(() => {
    // Initial check
    checkAllServices();

    // Set up interval for periodic checks (every 30 seconds for IRC, 10 minutes for others)
    const interval = setInterval(checkAllServices, 30000);

    return () => clearInterval(interval);
  }, [checkAllServices]);

  const getOverallStatus = (): ServiceStatus => {
    if (services.length === 0) return 'unknown';
    if (services.some(service => service.status === 'down')) return 'degraded';
    if (services.some(service => service.status === 'degraded')) return 'degraded';
    return 'operational';
  };

  const getStatusCounts = () => {
    const counts = {
      operational: 0,
      degraded: 0,
      down: 0,
      unknown: 0
    };

    // Count service cards
    services.forEach(service => {
      counts[service.status]++;
    });

    // Count IRC bots
    if (ircStatus) {
      Object.values(ircStatus.bots).forEach(bot => {
        if (bot.online) {
          counts.operational++;
        } else {
          counts.down++;
        }
      });
    }

    return counts;
  };

  const statusCounts = getStatusCounts();

  return (
    <div className="app">
      <Header 
        overallStatus={getOverallStatus()}
        lastUpdated={lastUpdated}
        statusCounts={statusCounts}
        isLoading={isLoading}
        onRefresh={checkAllServices}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
      
      <main className="main-content">
        {currentPage === 'seedpool' ? (
          <>
            <div className="services-grid">
              {services.map(service => (
                <ServiceCard 
                  key={service.id} 
                  service={service} 
                  isLoading={isLoading}
                  maintenanceMode={maintenanceMode}
                />
              ))}
            </div>
            
            {ircStatus && (
              <div className="irc-section">
                <h2 className="section-title">IRC Bot Status</h2>
                <div className="irc-info">
                  <span className={`irc-connection ${ircStatus.connected ? 'connected' : 'disconnected'}`}>
                    {ircStatus.connected ? '🟢 Connected' : '🔴 Disconnected'}
                  </span>
                  <span className="irc-stats">
                    {ircStatus.onlineBots}/{ircStatus.totalBots} bots online
                  </span>
                </div>
                <div className="bots-grid">
                  {Object.entries(ircStatus.bots).map(([botName, bot]) => (
                    <IRCBotCard 
                      key={botName}
                      bot={{...bot, nickname: botName}}
                      isLoading={isLoading}
                      maintenanceMode={maintenanceMode}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : currentPage === 'stats' ? (
          <div className="trackers-container">
            <TrackerStats 
              key="Seedpool"
              trackerName="Seedpool"
              stats={seedpoolStats}
              isLoading={seedpoolLoading}
              error={seedpoolError}
            />
          </div>
        ) : currentPage === 'history' ? (
          <History />
        ) : null}
      </main>

      <footer className="footer">
        <p>Powered by Seedpool.org • Last updated: {lastUpdated.toLocaleTimeString()}</p>
      </footer>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<MainContent />} />
        <Route path="/stats" element={<MainContent />} />
        <Route path="/foreign" element={<Navigate to="/stats" replace />} />
        <Route path="/history" element={<MainContent />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

export default App;